import React from 'react';
import { Card, Table, Tag, Button, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  EMAIL_EVENT_LABELS,
  EmailEventType,
  useListEmailTemplates,
} from '@shared/api/hooks/useEmailTemplates';

interface Row {
  key: string;
  eventType: EmailEventType;
  lang: 'it' | 'en';
  hasOverride: boolean;
  isActive: boolean;
  updatedAt: string | null;
}

const EVENTS: EmailEventType[] = [
  'WELCOME_VERIFY',
  'WELCOME_SSO',
  'ORDER_CONFIRMED',
  'ORDER_SHIPPED',
  'ORDER_DELIVERED',
  'ORDER_CANCELLED',
  'PASSWORD_RESET',
];

const LANGS: ('it' | 'en')[] = ['it', 'en'];

/**
 * Lista template email per tenant. Per ogni coppia (event, lang) mostra
 * se è in uso il default in-code o un override DB. Click → editor.
 */
export function EmailTemplatesPage(): React.ReactElement {
  const { data: overrides = [], isLoading } = useListEmailTemplates();

  const overrideKey = new Map(
    overrides.map((t) => [`${t.eventType}|${t.lang}`, t]),
  );

  const rows: Row[] = EVENTS.flatMap((eventType) =>
    LANGS.map((lang) => {
      const ov = overrideKey.get(`${eventType}|${lang}`);
      return {
        key: `${eventType}|${lang}`,
        eventType,
        lang,
        hasOverride: Boolean(ov),
        isActive: ov?.isActive ?? true,
        updatedAt: ov?.updatedAt ?? null,
      };
    }),
  );

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3} style={{ marginBottom: 4 }}>
        Template email
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Personalizza i testi delle email transazionali. I template senza override
        usano il default di sistema.
      </Typography.Paragraph>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<Row>
          rowKey="key"
          loading={isLoading}
          pagination={false}
          dataSource={rows}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: 'Evento',
              dataIndex: 'eventType',
              render: (v: EmailEventType) => EMAIL_EVENT_LABELS[v],
            },
            {
              title: 'Lingua',
              dataIndex: 'lang',
              width: 100,
              render: (v: string) => <Tag>{v.toUpperCase()}</Tag>,
            },
            {
              title: 'Stato',
              width: 180,
              render: (_, r) =>
                r.hasOverride ? (
                  r.isActive ? (
                    <Tag color="green">Override attivo</Tag>
                  ) : (
                    <Tag color="orange">Override disabilitato</Tag>
                  )
                ) : (
                  <Tag>Default sistema</Tag>
                ),
            },
            {
              title: 'Ultimo aggiornamento',
              dataIndex: 'updatedAt',
              width: 220,
              render: (v: string | null) =>
                v ? new Date(v).toLocaleString('it-IT') : '—',
            },
            {
              title: '',
              width: 100,
              align: 'right',
              render: (_, r) => (
                <Space>
                  <Link to={`/settings/email-templates/${r.eventType}/${r.lang}`}>
                    <Button icon={<EditOutlined />} size="small">
                      Modifica
                    </Button>
                  </Link>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
